import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { useUIStore } from '../../stores/ui-store';
import {
  ExerciseFormData,
  ExerciseCategory,
  DifficultyLevel,
  getExerciseCategoryName,
  getDifficultyLevelName,
  PlatformExerciseDetailResponse,
  PlatformExerciseSummaryResponse
} from './types';
import {
  useCreatePlatformExercise,
  useUpdatePlatformExercise,
  usePlatformEquipmentOptions
} from './api';

interface ExerciseFormProps {
  isOpen: boolean;
  onClose: () => void;
  exercise?: PlatformExerciseDetailResponse | PlatformExerciseSummaryResponse;
  mode: 'create' | 'edit';
}

const initialFormData: ExerciseFormData = {
  name: '',
  description: '',
  instructions: '',
  category: ExerciseCategory.Warmup,
  difficulty: DifficultyLevel.Beginner,
  primaryEquipmentId: 0,
  typicalDurationSeconds: undefined,
  defaultReps: undefined,
  defaultSpringSettings: '',
  commonPrimarySetups: '',
  displayOrder: 1,
  tags: '',
  modalities: '',
  isActive: true,
  alternativeEquipmentIds: [],
};

export function ExerciseForm({ isOpen, onClose, exercise, mode }: ExerciseFormProps) {
  const [formData, setFormData] = useState<ExerciseFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const { addNotification } = useUIStore();
  const createMutation = useCreatePlatformExercise();
  const updateMutation = useUpdatePlatformExercise();
  const { data: equipmentData } = usePlatformEquipmentOptions();

  // Reset form when modal opens/closes or exercise changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && exercise) {
        setFormData({
          name: exercise.name,
          description: exercise.description || '',
          instructions: 'instructions' in exercise ? exercise.instructions || '' : '',
          category: exercise.category,
          difficulty: exercise.difficulty,
          primaryEquipmentId: exercise.primaryEquipmentId,
          typicalDurationSeconds: exercise.typicalDurationSeconds,
          defaultReps: exercise.defaultReps,
          defaultSpringSettings: 'defaultSpringSettings' in exercise ? exercise.defaultSpringSettings || '' : '',
          commonPrimarySetups: 'commonPrimarySetups' in exercise ? exercise.commonPrimarySetups || '' : '',
          displayOrder: exercise.displayOrder,
          tags: exercise.tags || '',
          modalities: exercise.modalities || '',
          isActive: exercise.isActive,
          alternativeEquipmentIds: 'alternativeEquipment' in exercise 
            ? exercise.alternativeEquipment.map(alt => alt.equipmentId) 
            : [],
        });
      } else {
        setFormData(initialFormData);
      }
      setErrors({});
    }
  }, [isOpen, mode, exercise]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Exercise name is required';
    }

    if (!formData.primaryEquipmentId || formData.primaryEquipmentId === 0) {
      newErrors.primaryEquipmentId = 'Primary equipment is required';
    }

    if (formData.displayOrder < 1) {
      newErrors.displayOrder = 'Display order must be at least 1';
    }

    if (formData.typicalDurationSeconds && formData.typicalDurationSeconds < 1) {
      newErrors.typicalDurationSeconds = 'Duration must be at least 1 second';
    }

    if (formData.defaultReps && formData.defaultReps < 1) {
      newErrors.defaultReps = 'Default reps must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          instructions: formData.instructions.trim() || undefined,
          category: formData.category,
          difficulty: formData.difficulty,
          primaryEquipmentId: formData.primaryEquipmentId,
          typicalDurationSeconds: formData.typicalDurationSeconds || undefined,
          defaultReps: formData.defaultReps || undefined,
          defaultSpringSettings: formData.defaultSpringSettings.trim() || undefined,
          commonPrimarySetups: formData.commonPrimarySetups.trim() || undefined,
          displayOrder: formData.displayOrder,
          tags: formData.tags.trim() || undefined,
          modalities: formData.modalities.trim() || undefined,
          alternativeEquipmentIds: formData.alternativeEquipmentIds.length > 0 ? formData.alternativeEquipmentIds : undefined,
        });

        addNotification({
          type: 'success',
          title: 'Exercise Created',
          message: `${formData.name} has been created successfully.`,
        });
      } else {
        if (!exercise) return;

        await updateMutation.mutateAsync({
          id: exercise.id,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          instructions: formData.instructions.trim() || undefined,
          category: formData.category,
          difficulty: formData.difficulty,
          primaryEquipmentId: formData.primaryEquipmentId,
          typicalDurationSeconds: formData.typicalDurationSeconds || undefined,
          defaultReps: formData.defaultReps || undefined,
          defaultSpringSettings: formData.defaultSpringSettings.trim() || undefined,
          commonPrimarySetups: formData.commonPrimarySetups.trim() || undefined,
          displayOrder: formData.displayOrder,
          tags: formData.tags.trim() || undefined,
          modalities: formData.modalities.trim() || undefined,
          isActive: formData.isActive ?? true,
          alternativeEquipmentIds: formData.alternativeEquipmentIds.length > 0 ? formData.alternativeEquipmentIds : undefined,
        });

        addNotification({
          type: 'success',
          title: 'Exercise Updated',
          message: `${formData.name} has been updated successfully.`,
        });
      }

      onClose();
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: `Failed to ${mode === 'create' ? 'Create' : 'Update'} Exercise`,
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleInputChange = (field: keyof ExerciseFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAlternativeEquipmentChange = (equipmentId: number, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        alternativeEquipmentIds: [...prev.alternativeEquipmentIds, equipmentId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        alternativeEquipmentIds: prev.alternativeEquipmentIds.filter(id => id !== equipmentId)
      }));
    }
  };

  if (!isOpen) return null;

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const equipmentOptions = equipmentData?.equipment || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>
            {mode === 'create' ? 'Create New Exercise' : 'Edit Exercise'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={errors.name ? 'border-red-500' : ''}
                placeholder="Enter exercise name"
                disabled={isLoading}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Enter exercise description"
                disabled={isLoading}
              />
            </div>

            {/* Instructions */}
            <div>
              <label htmlFor="instructions" className="block text-sm font-medium text-gray-700 mb-1">
                Instructions
              </label>
              <textarea
                id="instructions"
                value={formData.instructions}
                onChange={(e) => handleInputChange('instructions', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Enter detailed exercise instructions"
                disabled={isLoading}
              />
            </div>

            {/* Category and Difficulty */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {Object.values(ExerciseCategory)
                    .filter(value => typeof value === 'number')
                    .map((category) => (
                      <option key={category} value={category}>
                        {getExerciseCategoryName(category as ExerciseCategory)}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty *
                </label>
                <select
                  id="difficulty"
                  value={formData.difficulty}
                  onChange={(e) => handleInputChange('difficulty', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading}
                >
                  {Object.values(DifficultyLevel)
                    .filter(value => typeof value === 'number')
                    .map((difficulty) => (
                      <option key={difficulty} value={difficulty}>
                        {getDifficultyLevelName(difficulty as DifficultyLevel)}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Primary Equipment */}
            <div>
              <label htmlFor="primaryEquipmentId" className="block text-sm font-medium text-gray-700 mb-1">
                Primary Equipment *
              </label>
              <select
                id="primaryEquipmentId"
                value={formData.primaryEquipmentId}
                onChange={(e) => handleInputChange('primaryEquipmentId', parseInt(e.target.value))}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.primaryEquipmentId ? 'border-red-500' : ''
                }`}
                disabled={isLoading}
              >
                <option value={0}>Select equipment...</option>
                {equipmentOptions.map((equipment) => (
                  <option key={equipment.id} value={equipment.id}>
                    {equipment.name}
                  </option>
                ))}
              </select>
              {errors.primaryEquipmentId && (
                <p className="text-red-500 text-xs mt-1">{errors.primaryEquipmentId}</p>
              )}
            </div>

            {/* Duration and Reps */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="typicalDurationSeconds" className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (seconds)
                </label>
                <Input
                  id="typicalDurationSeconds"
                  type="number"
                  min="1"
                  value={formData.typicalDurationSeconds || ''}
                  onChange={(e) => handleInputChange('typicalDurationSeconds', parseInt(e.target.value) || undefined)}
                  className={errors.typicalDurationSeconds ? 'border-red-500' : ''}
                  disabled={isLoading}
                />
                {errors.typicalDurationSeconds && (
                  <p className="text-red-500 text-xs mt-1">{errors.typicalDurationSeconds}</p>
                )}
              </div>

              <div>
                <label htmlFor="defaultReps" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Reps
                </label>
                <Input
                  id="defaultReps"
                  type="number"
                  min="1"
                  value={formData.defaultReps || ''}
                  onChange={(e) => handleInputChange('defaultReps', parseInt(e.target.value) || undefined)}
                  className={errors.defaultReps ? 'border-red-500' : ''}
                  disabled={isLoading}
                />
                {errors.defaultReps && (
                  <p className="text-red-500 text-xs mt-1">{errors.defaultReps}</p>
                )}
              </div>

              <div>
                <label htmlFor="displayOrder" className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order *
                </label>
                <Input
                  id="displayOrder"
                  type="number"
                  min="1"
                  value={formData.displayOrder}
                  onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value) || 1)}
                  className={errors.displayOrder ? 'border-red-500' : ''}
                  disabled={isLoading}
                />
                {errors.displayOrder && (
                  <p className="text-red-500 text-xs mt-1">{errors.displayOrder}</p>
                )}
              </div>
            </div>

            {/* Spring Settings and Setups */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="defaultSpringSettings" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Spring Settings
                </label>
                <Input
                  id="defaultSpringSettings"
                  type="text"
                  value={formData.defaultSpringSettings}
                  onChange={(e) => handleInputChange('defaultSpringSettings', e.target.value)}
                  placeholder="e.g., 2 Red, 1 Blue"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="commonPrimarySetups" className="block text-sm font-medium text-gray-700 mb-1">
                  Common Primary Setups
                </label>
                <Input
                  id="commonPrimarySetups"
                  type="text"
                  value={formData.commonPrimarySetups}
                  onChange={(e) => handleInputChange('commonPrimarySetups', e.target.value)}
                  placeholder="e.g., Long box, footbar up"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Tags and Modalities */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <Input
                  id="tags"
                  type="text"
                  value={formData.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  placeholder="e.g., core, flexibility, strength"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="modalities" className="block text-sm font-medium text-gray-700 mb-1">
                  Modalities
                </label>
                <Input
                  id="modalities"
                  type="text"
                  value={formData.modalities}
                  onChange={(e) => handleInputChange('modalities', e.target.value)}
                  placeholder="e.g., classical, contemporary"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Alternative Equipment */}
            {equipmentOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alternative Equipment
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {equipmentOptions
                    .filter(eq => eq.id !== formData.primaryEquipmentId)
                    .map((equipment) => (
                      <div key={equipment.id} className="flex items-center space-x-2 mb-1">
                        <input
                          type="checkbox"
                          id={`alt-equipment-${equipment.id}`}
                          checked={formData.alternativeEquipmentIds.includes(equipment.id)}
                          onChange={(e) => handleAlternativeEquipmentChange(equipment.id, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          disabled={isLoading}
                        />
                        <label htmlFor={`alt-equipment-${equipment.id}`} className="text-sm text-gray-700">
                          {equipment.name}
                        </label>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Is Active (only for edit mode) */}
            {mode === 'edit' && (
              <div className="flex items-center space-x-2">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={isLoading}
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Active
                </label>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="min-w-[80px]"
              >
                {isLoading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}